//============================================================================
// Name        : sidebar.ts
// Purpose     : Implement inventory page sidebar logic
// Created     : 2020-01-02
// Author      : Senitron Team
// Copyright   : Copyright 2020 Senitron Corporation. All rights reserved.
//============================================================================

import axios from "axios";
import * as QueryString from "query-string";
import {ICustomNodeOptions, SeTreeview} from "../controls/se_treeview";
import {InitPage} from "../index";

declare var storeLocation: string;

declare var inventoryUrl: string;
declare var loadCategoriesUrl: string;
declare var loadLastSeenUrl: string;
declare var inventoryStatsUrl: string;
declare var inventoryCountZones: string;
declare var inventoryCountStatus: string;
declare var inventoryCountBrand: string;
declare var inventoryCountCategories: string;
declare var inventoryCountItemTypes: string;

declare var inventoryFetchZones: string;
declare var inventoryFetchStatus: string;
declare var inventoryFetchBrand: string;
declare var inventoryFetchItemTypes: string;
declare var inventoryFetchItemsOut: string;

declare var paramsObj: object;

interface IZoneStat {
    id: any;
    count: number;
}

interface IStatusStat {
    id: number;
    count: number;
}

interface IBrandStat {
    id: string;
    count: number;
}

interface IItemTypeStat {
    id: number;
    count: number;
}

interface IInventoryStats {
    total_count: number;
    item_count: number;
    zones: IZoneStat[];
    status: IStatusStat[];
    brand: IBrandStat[];
    item_types: IItemTypeStat[];
}

export function LoadPage() {
    InitPage("epc_tags inventory_logs", () => {
        const treeview = new SeTreeview();
        treeview.setListCounter("zones", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountZones);
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            // Update zone counts
            const zonesList = $(".zones-list ul");
            for (const z of resp.data.zones) {
                if (z.id == "unassigned") {
                    const rootel = $(".zones-list").find(`[data-record-id='${z.id}']`).find("div");
                    rootel.html(` (${z.count})`);
                    rootel.parent().data("count", z.count);
                    continue;
                }

                const el = zonesList.find(`[data-record-id='${z.id}']`).find("div");
                el.html(` (${z.count})`);
                el.parent().data("count", z.count);
            }

            // Add up zone counts
            const zones = zonesList.toArray();
            for (const zone of zones) {
                let count = 0;
                for (const node of Array.from(zone.children)) {
                    count += $(node).children().last().data("count");
                }
                $(zone).prev("label").find("div").html(` (${count})`);
            }
            treeview.countFinished("zones");
        });
        treeview.setListCounter("status", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountStatus);
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            // Update status counts
            for (const s of resp.data.status) {
                const el = $(".status-list").find(`[data-record-id='${s.id}']`).find("div");
                el.html(` (${s.count})`);
                el.parent().data("count", s.count);
            }
            treeview.countFinished("status");
        });
        treeview.setListCounter("brands", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountBrand);
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            // Update brand counts
            for (const s of resp.data.brand) {
                const el = $(".brands-list").find(`[data-record-id='${treeview.safeHtml(s.id)}']`).find("div");
                el.html(` (${s.count})`);
                el.parent().data("count", s.count);
            }
            treeview.countFinished("brands");
        });
        treeview.setListCounter("categories", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountCategories + ".json");
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            const updateCounterFunc = (cats) => {
                // Update category counts
                for (const cat of cats) {
                    const el = $(".categories-list").find(`[data-record-id='${cat.id}']`).find("div");
                    el.html(` (${cat.count})`);
                    el.parent().data("count", cat.count);

                    updateCounterFunc(cat.children);
                }
                treeview.countFinished("categories");
            };
            updateCounterFunc(resp.data);
        });
        treeview.setListCounter("type", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountItemTypes);
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            // Update item type counts
            for (const t of resp.data.itemTypes) {
                const el = $(".type-list").find(`[data-record-id='${t.id}']`).find("div");
                el.html(` (${t.count})`);
                el.parent().data("count", t.count);
            }
            treeview.countFinished("type");
        });

        // Load and count zones
        const loadAssetCountFunc = async () => {
            (paramsObj as any).stats_count = true;
            const resp = await axios.get(inventoryStatsUrl, { params: paramsObj });
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            const stats: IInventoryStats = resp.data;

            // Update total asset count
            $("#totalAssetInventoryCount").text(stats.total_count);
            $("#totalItemInventoryCount").text(stats.item_count);
        };

        // Async load all the sidebar data on a delay
        setTimeout(
            () => {
                loadAssetCountFunc().then();

                treeview.loadNodes("zones", inventoryFetchZones, {
                    altNodeName: "zone",
                    childrenNodeName: "sub_zone"
                } as ICustomNodeOptions).then(() => treeview.setCheckedNodes("zones", {
                    altNodeName: "zone",
                    childrenNodeName: "sub_zone"
                } as ICustomNodeOptions));

                treeview.loadNodes("categories", loadCategoriesUrl + ".json")
                        .then(() => treeview.setCheckedNodes("categories"));

                treeview.loadNodes("status", inventoryFetchStatus)
                        .then(() => treeview.setCheckedNodes("status"));

                treeview.loadNodes("brands", inventoryFetchBrand)
                    .then(() => treeview.setCheckedNodes("brands"));

                treeview.loadNodes("last_seen", loadLastSeenUrl)
                        .then(() => treeview.setCheckedNodes("last_seen"));

                treeview.loadNodes("type", inventoryFetchItemTypes, {
                    altNodeName: "sort_item",
                    childrenNodeName: "sort_item"
                } as ICustomNodeOptions).then(() => treeview.setCheckedNodes("type", {
                    altNodeName: "sort_item"
                } as ICustomNodeOptions));

                treeview.loadNodes("items_out", inventoryFetchItemsOut)
                        .then(() => treeview.setCheckedNodes("items_out"));
            },
            1000 // Initial delay before populating remaining sections
        );

        // On page load keep track of the filtering options
        const currentFilters  = QueryString.parse(location.search);
        delete currentFilters.utf8;
        let savedFiltersStr = localStorage.getItem("inventoryFilters");

        // Check if we've switched locations, in that case invalidate the filters
        // to prevent issues, on load.
        if (savedFiltersStr) {
            const savedFilters = QueryString.parse(savedFiltersStr);
            if (savedFilters.location != storeLocation) {
                localStorage.removeItem("inventoryFilters");
                location.href = inventoryUrl;
                return;
            }
        }

        // Should restore filters
        if (Object.keys(currentFilters).length <= 2 && savedFiltersStr) {
            const savedFilters = QueryString.parse(savedFiltersStr);
            if (savedFilters.utf8) {
                delete savedFilters.utf8;
            }
            if (!currentFilters.search) {
                if (savedFilters.search) {
                    delete savedFilters.search;
                }
                if (Object.keys(currentFilters).length == Object.keys(savedFilters).length) {
                    return;
                }
            }
            else {
                if (!location.search.includes("filter")) {
                    savedFilters.search = currentFilters.search;
                    savedFilters.multilocation = currentFilters.multilocation;
                    savedFilters.location = storeLocation;
                    savedFiltersStr = QueryString.stringify(savedFilters);
                }
            }
            if (!location.search.includes("antenna_id")) {
                location.search = savedFiltersStr;
            }
        }

        // Should save new filters
        delete currentFilters.search;
        delete currentFilters.multilocation;
        if (Object.keys(currentFilters).length > 1) {
            localStorage.setItem("inventoryFilters", location.search + `&location=${storeLocation}`);
        }
    });

    InitPage("epc_tags", () => {
        // Handle clearing inventory filters
        $(".clear-filters").on("click", () => {
            console.log("Clearing filters");
            localStorage.removeItem("inventoryFilters");
            location.href = inventoryUrl;
        });
        window.onbeforeunload =  (e) => {
            window.onunload = () => {
                window.localStorage.removeItem("inventoryFilters");
            }
            return undefined;
        };
    });
}
